import React from 'react';
import type { DateRangeFilter as DateRangeFilterValue } from '@app-types';
import { formatMediumCalendarDate } from '@utils/dateUtils';
import RangeFilter from './RangeFilter';

export interface DateRangeFilterProps {
  columnLabel: string;
  value?: DateRangeFilterValue;
  onChange: (value: DateRangeFilterValue | null) => void;
}

/** Same wording the date cells in this column use, so the two read as one thing. */
const day = (value: string) => formatMediumCalendarDate(value, value);

/**
 * How a set range reads on the closed trigger.
 *
 * Within one year the year is stated once — "Jan 10 – Dec 31, 2025" rather than
 * repeating it — because the trigger is a column-width control and the repeated
 * year is the first thing that gets cut off.
 */
function summariseDates(from: string, to: string): string {
  if (from && to) {
    const sameYear = from.slice(0, 4) === to.slice(0, 4);
    return `${sameYear ? day(from).replace(/,\s*\d{4}$/, '') : day(from)} – ${day(to)}`;
  }
  if (from) return `From ${day(from)}`;
  if (to) return `Until ${day(to)}`;
  return '';
}

/**
 * An inclusive date range for a date column.
 *
 * An end before the start is refused with a message (FR-015 / US2.4) rather than
 * submitted to return a confusingly empty list. Everything else — the panel, the
 * draft, Apply — belongs to {@link RangeFilter}, which the amount filter shares.
 */
const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ columnLabel, value, onChange }) => (
  <RangeFilter
    columnLabel={columnLabel}
    inputType="date"
    emptyLabel="Any date"
    value={value}
    onChange={(bounds) => onChange(bounds && { kind: 'dateRange', ...bounds })}
    validate={(from, to) =>
      from && to && to < from ? 'End date must not be before the start date' : null
    }
    summarise={summariseDates}
  />
);

export default DateRangeFilter;
